/**
 * Plain-language summaries of chart data, used for the "Read chart" option
 * (spoken with speechSynthesis) and as the screen-reader description of each chart.
 */

export interface DescribeSeries {
  key: string;
  label?: string;
}

export interface DescribeOptions {
  title?: string;
  valueFormatter?: (v: number) => string;
  xFormatter?: (v: string) => string;
}

const num = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

function fmt(v: number, f?: (v: number) => string): string {
  return f ? f(v) : Number.isInteger(v) ? String(v) : v.toFixed(2);
}

/** "Invoices per day: 30 points from 1 Sep to 30 Sep. Issued: total 412, highest 31 on 12 Sep, lowest 3 on 4 Sep, latest 18." */
export function describeSeries(
  data: Record<string, unknown>[],
  xKey: string,
  series: DescribeSeries[],
  opts: DescribeOptions = {},
): string {
  const x = (row: Record<string, unknown>) => {
    const raw = String(row[xKey] ?? "");
    return opts.xFormatter ? opts.xFormatter(raw) : raw;
  };
  const head = opts.title ? `${opts.title}: ` : "";
  if (data.length === 0) return `${head}no data.`;
  const first = data[0];
  const last = data[data.length - 1];
  const span =
    data.length === 1 || !first || !last
      ? `${data.length} point`
      : `${data.length} points from ${x(first)} to ${x(last)}`;
  const parts = series.map((s) => {
    const name = s.label ?? s.key;
    const points = data
      .map((row) => ({ x: x(row), v: num(row[s.key]) }))
      .filter((p): p is { x: string; v: number } => p.v !== null);
    if (points.length === 0) return `${name}: no values`;
    const total = points.reduce((a, p) => a + p.v, 0);
    const hi = points.reduce((a, p) => (p.v > a.v ? p : a));
    const lo = points.reduce((a, p) => (p.v < a.v ? p : a));
    const latest = points[points.length - 1] as { x: string; v: number };
    const f = (v: number) => fmt(v, opts.valueFormatter);
    if (points.length === 1) return `${name}: ${f(latest.v)}`;
    return `${name}: total ${f(total)}, highest ${f(hi.v)} on ${hi.x}, lowest ${f(lo.v)} on ${lo.x}, latest ${f(latest.v)}`;
  });
  return `${head}${span}. ${parts.join(". ")}.`;
}

/** "App versions: 3 slices, total 120. 0.1.2: 80 (67%). 0.1.1: 30 (25%). 0.1.0: 10 (8%)." */
export function describeShares(
  data: { name: string; value: number }[],
  opts: DescribeOptions = {},
): string {
  const head = opts.title ? `${opts.title}: ` : "";
  if (data.length === 0) return `${head}no data.`;
  const total = data.reduce((a, d) => a + (num(d.value) ?? 0), 0);
  const f = (v: number) => fmt(v, opts.valueFormatter);
  const slices = [...data]
    .sort((a, b) => b.value - a.value)
    .map((d) => {
      const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
      return `${d.name}: ${f(d.value)} (${pct}%)`;
    });
  return `${head}${data.length} slice${data.length === 1 ? "" : "s"}, total ${f(total)}. ${slices.join(". ")}.`;
}
